import { message, Tooltip } from 'antd';
import { useCallback, useRef, useState } from 'react';

import type {
  GroupedImageUploadField,
  ImageUploadField,
} from '@/3.0/ai-viral-template/_components/template-ui/types';
import {
  isSupportedViralTemplateImageType,
  VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT,
  VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
} from '@/3.0/ai-viral-template/constants/imageUploadConstraints';
import Loading from '@/components/common/Loading';
import { uploadFile } from '@/components/VideoClipper/utils/ffmpeg/utils';
import type { UseTranslateType } from '@/hooks/useTranslate';
import { AIFunction } from '@/utils/const';
import { EventNewID } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { getImageInfo } from '@/utils/fileUtil';

import { RequiredMark } from './TemplateFormFields';

const CELL_PX = 80;

const BRAND_BORDER = 'var(--app-accent)';

export type ImageSlotConstraints = Pick<
  typeof VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
  | 'minWidth'
  | 'maxWidth'
  | 'minHeight'
  | 'maxHeight'
  | 'maxSizeBytes'
  | 'minAspectRatio'
  | 'maxAspectRatio'
  | 'minAspectRatioText'
  | 'maxAspectRatioText'
>;

function bytesToMb(size: number): number {
  return Math.round((size / (1024 * 1024)) * 100) / 100;
}

function UploadRequirementTooltip({
  constraints,
  t,
  children,
}: {
  constraints: ImageSlotConstraints;
  t: UseTranslateType;
  children: React.ReactNode;
}) {
  const tooltip = (
    <div className="p-[12px] text-app-primary">
      <div className="mb-[6px] text-[12px] font-semibold leading-[20px]">
        {t('viralTemplate.form.uploadRestrictions')}
      </div>
      <ul className="m-0 list-disc pl-[18px]">
        <li>
          {t('viralTemplate.form.imageFileSizeRangeInvalid', {
            min: 0,
            max: bytesToMb(constraints.maxSizeBytes),
          })}
        </li>
        <li>
          {t('viralTemplate.form.imageWidthRangeInvalid', {
            min: constraints.minWidth,
            max: constraints.maxWidth,
          })}
        </li>
        <li>
          {t('viralTemplate.form.imageHeightRangeInvalid', {
            min: constraints.minHeight,
            max: constraints.maxHeight,
          })}
        </li>
        <li>
          {t('viralTemplate.form.imageRatioInvalid', {
            min: constraints.minAspectRatioText,
            max: constraints.maxAspectRatioText,
          })}
        </li>
      </ul>
    </div>
  );

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      mouseEnterDelay={1}
      // align={{
      //   offset: [15, -10],
      // }}
      color="var(--app-bg-card)"
      styles={{
        body: {
          fontSize: '12px',
          lineHeight: '20px',
          padding: 0,
          width: 'fit-content',
        },
      }}
    >
      {children}
    </Tooltip>
  );
}

function loadImageDims(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function useValidateAndUploadImage(
  constraints: ImageSlotConstraints,
  t: UseTranslateType,
) {
  return useCallback(
    async (file: File, onOk: (assetUrl: string) => void) => {
      if (!isSupportedViralTemplateImageType(file)) {
        void message.error(
          t('rejectedNames-accept', {
            rejectedNames: file.name,
            accept: VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT,
          }),
        );
        return;
      }

      const c = constraints;
      if (file.size > c.maxSizeBytes) {
        void message.error(
          t('viralTemplate.form.imageFileSizeRangeInvalid', {
            min: 0,
            max: 10,
          }),
        );
        return;
      }

      const objUrl = URL.createObjectURL(file);
      try {
        const { w, h } = await loadImageDims(objUrl);
        if (w < c.minWidth || w > c.maxWidth) {
          void message.error(
            t('viralTemplate.form.imageWidthRangeInvalid', {
              min: c.minWidth,
              max: c.maxWidth,
            }),
          );
          return;
        }
        if (h < c.minHeight || h > c.maxHeight) {
          void message.error(
            t('viralTemplate.form.imageHeightRangeInvalid', {
              min: c.minHeight,
              max: c.maxHeight,
            }),
          );
          return;
        }
        const ratio = w / h;
        if (ratio < c.minAspectRatio || ratio > c.maxAspectRatio) {
          void message.error(
            t('viralTemplate.form.imageRatioInvalid', {
              min: c.minAspectRatioText,
              max: c.maxAspectRatioText,
            }),
          );
          return;
        }
      } catch {
        void message.error(t('viralTemplate.form.imageLoadFailed'));
        return;
      } finally {
        URL.revokeObjectURL(objUrl);
      }

      try {
        const url = await uploadFile(file);
        if (!url?.trim()) {
          void message.error(t('merror.tip.uploadFailed'));
          return;
        }
        onOk(url.trim());
      } catch {
        void message.error(t('merror.tip.uploadFailed'));
      }
    },
    [constraints, t],
  );
}

const ReloadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M10 16H5V21M14 8H19V3M4.58301 9.0034C5.14369 7.61566 6.08244 6.41304 7.29255 5.53223C8.50266 4.65141 9.93686 4.12752 11.4298 4.02051C12.9227 3.9135 14.4147 4.2274 15.7381 4.92661C17.0615 5.62582 18.1612 6.68254 18.9141 7.97612M19.4176 14.9971C18.8569 16.3848 17.9181 17.5874 16.708 18.4682C15.4979 19.3491 14.0652 19.8723 12.5723 19.9793C11.0794 20.0863 9.58606 19.7725 8.2627 19.0732C6.93933 18.374 5.83882 17.3175 5.08594 16.0239"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M9.33305 6.66667V11.3333M6.66638 6.66667V11.3333M3.99972 4V11.8667C3.99972 12.6134 3.99972 12.9865 4.14504 13.2717C4.27287 13.5226 4.4767 13.727 4.72758 13.8548C5.01252 14 5.38571 14 6.13099 14H9.86844C10.6137 14 10.9864 14 11.2713 13.8548C11.5222 13.727 11.7267 13.5226 11.8545 13.2717C11.9997 12.9868 11.9997 12.614 11.9997 11.8687V4M3.99972 4H5.33305M3.99972 4H2.66638M5.33305 4H10.6664M5.33305 4C5.33305 3.37874 5.33305 3.06827 5.43454 2.82324C5.56987 2.49654 5.82926 2.23682 6.15597 2.10149C6.40099 2 6.71179 2 7.33305 2H8.66638C9.28764 2 9.59827 2 9.84329 2.10149C10.17 2.23682 10.4295 2.49654 10.5648 2.82324C10.6663 3.06827 10.6664 3.37875 10.6664 4M10.6664 4H11.9997M11.9997 4H13.333"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function FilledSlot({
  url,
  slotIndex,
  accept,
  t,
  constraints,
  pickFileForSlot,
  onClear,
  isUploading,
  containerTestId,
  replaceButtonTestId,
  deleteButtonTestId,
}: {
  url: string;
  slotIndex: number;
  accept: string;
  t: UseTranslateType;
  constraints: ImageSlotConstraints;
  pickFileForSlot: (index: number, file: File) => void;
  onClear: () => void;
  isUploading: boolean;
  containerTestId?: string;
  replaceButtonTestId?: string;
  deleteButtonTestId?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onReplaceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    inputRef.current?.click();
  };

  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) pickFileForSlot(slotIndex, f);
  };

  return (
    <UploadRequirementTooltip constraints={constraints} t={t}>
      <div
        data-testid={containerTestId}
        className="group relative shrink-0 overflow-hidden rounded-[6px] border-[1.5px] border-solid bg-app-input"
        style={{ width: 108, height: 148, borderColor: BRAND_BORDER }}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          onChange={onChange}
        />
        <img src={url} alt="" className="absolute inset-0 size-full object-contain" />
        {isUploading ? (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center rounded-[6px] bg-black/45"
            aria-busy
            aria-label={t('merror.language.uploading')}
          >
            <Loading size={16} />
          </div>
        ) : null}
        <div
          className="pointer-events-none absolute inset-0 rounded-[6px] bg-black/0 transition-colors duration-150 group-hover:bg-black/40"
          aria-hidden
        />
        <button
          type="button"
          data-testid={replaceButtonTestId}
          className="absolute left-1/2 top-1/2 z-20 flex size-[30px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[4px] bg-[rgba(255,255,255,0.34)] opacity-0 backdrop-blur-[2px] transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100"
          onClick={onReplaceClick}
        >
          <ReloadIcon />
        </button>
        <button
          type="button"
          data-testid={deleteButtonTestId}
          className="absolute right-[4px] top-[4px] z-30 flex size-[20px] items-center justify-center rounded-[4px] bg-black/40 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100"
          onClick={onDeleteClick}
        >
          <TrashIcon />
        </button>
      </div>
    </UploadRequirementTooltip>
  );
}

function EmptySlot({
  t,
  slotIndex,
  accept,
  constraints,
  pickFileForSlot,
  isUploading,
  buttonTestId,
}: {
  t: UseTranslateType;
  slotIndex: number;
  accept: string;
  constraints: ImageSlotConstraints;
  pickFileForSlot: (index: number, file: File) => void;
  isUploading: boolean;
  buttonTestId?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = () => {
    inputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) pickFileForSlot(slotIndex, f);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={onChange}
      />
      <UploadRequirementTooltip constraints={constraints} t={t}>
        <button
          type="button"
          data-testid={buttonTestId}
          onClick={onPick}
          disabled={isUploading}
          className="flex relative shrink-0 flex-col items-center justify-center gap-[2px] rounded-[4px] border border-dashed border-app bg-app-input text-app-tertiary transition-colors hover:border-app-accent hover:border-solid hover:text-app-primary disabled:pointer-events-none disabled:opacity-60"
          style={{ width: 108, height: 148 }}
          aria-busy={isUploading}
          aria-label={isUploading ? t('merror.language.uploading') : undefined}
        >
          {isUploading ? (
            <Loading size={18} />
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                className="shrink-0"
                aria-hidden
              >
                <path
                  d="M15.9295 9.94498H9.94449V15.93C9.94449 16.278 9.52145 16.875 8.99949 16.875C8.47785 16.875 8.05449 16.278 8.05449 15.93V9.94498H2.06951C1.72175 9.94498 1.12451 9.52193 1.12451 8.99998C1.12451 8.47803 1.72175 8.05498 2.06951 8.05498H8.05449V2.07C8.05449 1.72224 8.47785 1.125 8.99949 1.125C9.52145 1.125 9.94449 1.72224 9.94449 2.07V8.05498H15.9295C16.2772 8.05498 16.8745 8.47803 16.8745 8.99998C16.8745 9.52193 16.2772 9.94498 15.9295 9.94498Z"
                  fill="currentColor"
                />
              </svg>
              <span className="text-[14px] font-medium leading-[20px] text-inherit">
                {t('merror.operate.upload')}
              </span>
            </>
          )}
        </button>
      </UploadRequirementTooltip>
    </>
  );
}

function ImageSlotsRow({
  urls,
  setUrls,
  accept,
  t,
  constraints,
  wrap = true,
  filledSlotTestIdPrefix,
  replaceButtonTestIdPrefix,
  deleteButtonTestIdPrefix,
  emptySlotTestIdPrefix,
}: {
  urls: string[];
  setUrls: React.Dispatch<React.SetStateAction<string[]>>;
  accept: string;
  t: UseTranslateType;
  constraints: ImageSlotConstraints;
  wrap?: boolean;
  filledSlotTestIdPrefix?: string;
  replaceButtonTestIdPrefix?: string;
  deleteButtonTestIdPrefix?: string;
  emptySlotTestIdPrefix?: string;
}) {
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const validateAndUpload = useValidateAndUploadImage(constraints, t);

  const pickFileForSlot = useCallback(
    async (index: number, file: File) => {
      setUploadingIndex(index);
      try {
        await validateAndUpload(file, (assetUrl) => {
          getImageInfo(assetUrl).then(({ width, height }) => {
            const resolution = `${width}x${height}`;
            trackEvent(EventNewID.ASSET_UPLOAD, {
              resolution,
              source: AIFunction.ViralTemplate,
            });
          });

          setUrls((prev) => {
            const next = [...prev];
            next[index] = assetUrl;
            return next;
          });
        });
      } finally {
        setUploadingIndex(null);
      }
    },
    [validateAndUpload, setUrls],
  );

  const clearSlot = useCallback(
    (index: number) => {
      setUrls((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
    },
    [setUrls],
  );

  return (
    <div className={`flex items-start gap-[12px] ${wrap ? 'flex-wrap' : 'flex-nowrap'}`}>
      {urls.map((url, i) =>
        url ? (
          <FilledSlot
            key={`slot-${i}-${url.slice(0, 32)}`}
            url={url}
            slotIndex={i}
            accept={accept}
            t={t}
            constraints={constraints}
            pickFileForSlot={pickFileForSlot}
            onClear={() => clearSlot(i)}
            isUploading={uploadingIndex === i}
            containerTestId={
              filledSlotTestIdPrefix ? `${filledSlotTestIdPrefix}-${i}` : undefined
            }
            replaceButtonTestId={
              replaceButtonTestIdPrefix ? `${replaceButtonTestIdPrefix}-${i}` : undefined
            }
            deleteButtonTestId={
              deleteButtonTestIdPrefix ? `${deleteButtonTestIdPrefix}-${i}` : undefined
            }
          />
        ) : (
          <EmptySlot
            key={`empty-${i}`}
            slotIndex={i}
            accept={accept}
            t={t}
            constraints={constraints}
            pickFileForSlot={pickFileForSlot}
            isUploading={uploadingIndex === i}
            buttonTestId={
              emptySlotTestIdPrefix ? `${emptySlotTestIdPrefix}-${i}` : undefined
            }
          />
        ),
      )}
    </div>
  );
}

function UploadReferenceIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="42"
      height="42"
      viewBox="0 0 42 42"
      fill="none"
    >
      <path
        d="M29.3992 25.0027L24.8062 21.5582C24.1102 21.0365 23.1402 21.0365 22.4442 21.5582L18.4008 24.5901L13.0102 20.2777C12.3342 19.7367 11.3991 19.6957 10.6801 20.1797L5.25 23.8309V9.1875H34.125V23.625C34.125 24.3497 34.7128 24.9375 35.4375 24.9375C36.1622 24.9375 36.75 24.3497 36.75 23.625V9.07717C36.75 7.69043 35.6221 6.5625 34.2353 6.5625H5.13967C3.75293 6.5625 2.625 7.69043 2.625 9.07717V32.9228C2.625 34.3096 3.75293 35.4375 5.13967 35.4375H24.9375C25.6622 35.4375 26.25 34.8497 26.25 34.125C26.25 33.4003 25.6622 32.8125 24.9375 32.8125H5.25V26.9948L11.7452 22.6275L18.3496 27.9103L23.625 23.9531L27.8242 27.1023C28.4062 27.5399 29.2281 27.4194 29.6617 26.8402C30.0964 26.2603 29.9791 25.4375 29.3992 25.0027Z"
        fill="currentColor"
        fillOpacity="0.68"
      />
      <path
        d="M23.625 15.75C23.625 17.9214 25.3911 19.6875 27.5625 19.6875C29.7339 19.6875 31.5 17.9214 31.5 15.75C31.5 13.5786 29.7339 11.8125 27.5625 11.8125C25.3911 11.8125 23.625 13.5786 23.625 15.75ZM28.875 15.75C28.875 16.4735 28.286 17.0625 27.5625 17.0625C26.839 17.0625 26.25 16.4735 26.25 15.75C26.25 15.0265 26.839 14.4375 27.5625 14.4375C28.286 14.4375 28.875 15.0265 28.875 15.75ZM38.0625 31.5H35.4375V28.875C35.4375 28.1503 34.8497 27.5625 34.125 27.5625C33.4003 27.5625 32.8125 28.1503 32.8125 28.875V31.5H30.1875C29.4628 31.5 28.875 32.0878 28.875 32.8125C28.875 33.5372 29.4628 34.125 30.1875 34.125H32.8125V36.75C32.8125 37.4747 33.4003 38.0625 34.125 38.0625C34.8497 38.0625 35.4375 37.4747 35.4375 36.75V34.125H38.0625C38.7872 34.125 39.375 33.5372 39.375 32.8125C39.375 32.0878 38.7872 31.5 38.0625 31.5Z"
        fill="currentColor"
        fillOpacity="0.68"
      />
    </svg>
  );
}

function WorkbenchCollapsedEmptyState({
  t,
  accept,
  constraints,
  pickFileForSlot,
  isUploading,
}: {
  t: UseTranslateType;
  accept: string;
  constraints: ImageSlotConstraints;
  pickFileForSlot: (index: number, file: File) => void;
  isUploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = () => {
    inputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) pickFileForSlot(0, f);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={onChange}
      />
      <UploadRequirementTooltip constraints={constraints} t={t}>
        <button
          type="button"
          data-testid="viral-template-reference-upload-empty"
          onClick={onPick}
          disabled={isUploading}
          className="relative flex min-h-[148px] w-full items-center justify-center rounded-[20px] bg-transparent px-[24px] py-[20px] text-app-tertiary disabled:pointer-events-none disabled:opacity-60"
          aria-busy={isUploading}
          aria-label={isUploading ? t('merror.language.uploading') : undefined}
        >
          {isUploading ? (
            <Loading size={20} />
          ) : (
            <div className="flex items-center justify-center gap-[24px] text-center">
              <UploadReferenceIcon />
              <span className="text-[14px] font-[500] leading-[24px] text-app-secondary">
                {t('b9013268a1c2f6aad6aae112d2caa844')}
              </span>
            </div>
          )}
        </button>
      </UploadRequirementTooltip>
    </>
  );
}

export function WorkbenchReferenceImageField({
  field,
  value,
  onChange,
  t,
}: {
  field: GroupedImageUploadField | ImageUploadField;
  value: unknown;
  onChange: (next: string[] | string) => void;
  t: UseTranslateType;
}) {
  const urls =
    field.type === 'groupedImageUpload'
      ? field.slots.map((_, i) =>
          String((Array.isArray(value) ? value[i] : undefined) ?? '').trim(),
        )
      : [String(value ?? '').trim()];

  const setUrls = useCallback(
    (next: React.SetStateAction<string[]>) => {
      const resolved = typeof next === 'function' ? next(urls) : next;
      if (field.type === 'groupedImageUpload') {
        onChange(field.slots.map((_, i) => String(resolved[i] ?? '').trim()));
        return;
      }
      onChange(String(resolved[0] ?? '').trim());
    },
    [field, onChange, urls],
  );

  const constraints: ImageSlotConstraints = {
    ...VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
  };
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const validateAndUpload = useValidateAndUploadImage(constraints, t);

  const pickFileForSlot = useCallback(
    async (index: number, file: File) => {
      setUploadingIndex(index);
      try {
        await validateAndUpload(file, (assetUrl) => {
          getImageInfo(assetUrl).then(({ width, height }) => {
            const resolution = `${width}x${height}`;
            trackEvent(EventNewID.ASSET_UPLOAD, {
              resolution,
              source: AIFunction.ViralTemplate,
            });
          });

          setUrls((prev) => {
            const next = [...prev];
            next[index] = assetUrl;
            return next;
          });
        });
      } finally {
        setUploadingIndex(null);
      }
    },
    [setUrls, validateAndUpload],
  );

  const uploadedCount = urls.filter(Boolean).length;

  if (uploadedCount === 0) {
    return (
      <WorkbenchCollapsedEmptyState
        t={t}
        accept={VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT}
        constraints={constraints}
        pickFileForSlot={pickFileForSlot}
        isUploading={uploadingIndex === 0}
      />
    );
  }

  return (
    <div
      data-testid="viral-template-reference-upload-populated"
      className="flex min-h-[148px] w-full items-center gap-[12px]"
    >
      <ImageSlotsRow
        urls={urls}
        setUrls={setUrls}
        accept={VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT}
        constraints={constraints}
        t={t}
        wrap={false}
        filledSlotTestIdPrefix="viral-template-reference-filled"
        replaceButtonTestIdPrefix="viral-template-reference-replace"
        deleteButtonTestIdPrefix="viral-template-reference-delete"
        emptySlotTestIdPrefix="viral-template-reference-empty-slot"
      />
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-[500] leading-[24px] text-app-primary">
          {field.required ? <RequiredMark /> : null}
          {t('b9013268a1c2f6aad6aae112d2caa844')}
        </div>
      </div>
    </div>
  );
}

/** 多槽参考图（默认预览 + 上传 / hover 替换与删除） */
export function GroupedImageUploadFieldRow({
  field,
  value,
  onChange,
  t,
  hideLabel = false,
}: {
  field: GroupedImageUploadField;
  /** 长度与 `slots` 一致，由父组件统一维护 */
  value: string[];
  onChange: (next: string[]) => void;
  t: UseTranslateType;
  hideLabel?: boolean;
}) {
  const urls = field.slots.map((_, i) => String(value[i] ?? '').trim());
  const setUrls = useCallback(
    (next: React.SetStateAction<string[]>) => {
      const resolved = typeof next === 'function' ? next(urls) : next;
      onChange(field.slots.map((_, i) => String(resolved[i] ?? '').trim()));
    },
    [field.slots, onChange, urls],
  );

  const constraints: ImageSlotConstraints = {
    ...VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
  };

  return (
    <div className="flex w-full flex-col gap-[8px]">
      {!hideLabel ? (
        <div className="flex h-[20px] items-center">
          <span className="whitespace-nowrap text-[14px] font-medium leading-[20px] text-app-primary">
            {t(field.label)}
            {field.required ? <RequiredMark /> : null}
          </span>
        </div>
      ) : null}
      <ImageSlotsRow
        urls={urls}
        setUrls={setUrls}
        accept={VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT}
        constraints={constraints}
        t={t}
      />
    </div>
  );
}

/** 单图：同一套 88×88 交互，仅 1 槽 */
export function SingleImageUploadField({
  field,
  value,
  onChange,
  t,
  hideLabel = false,
}: {
  field: ImageUploadField;
  value: string;
  onChange: (next: string) => void;
  t: UseTranslateType;
  hideLabel?: boolean;
}) {
  const urls: string[] = [String(value ?? '').trim()];
  const setUrls = useCallback(
    (next: React.SetStateAction<string[]>) => {
      const prev = urls;
      const resolved = typeof next === 'function' ? next(prev) : next;
      onChange(String(resolved[0] ?? '').trim());
    },
    [onChange, urls],
  );

  const constraints: ImageSlotConstraints = {
    ...VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
  };

  return (
    <div className="flex w-full flex-col gap-[8px]">
      {!hideLabel ? (
        <div className="flex h-[20px] items-center">
          <span className="whitespace-nowrap text-[14px] font-medium leading-[20px] text-app-primary">
            {t(field.label)}
            {field.required ? <RequiredMark /> : null}
          </span>
        </div>
      ) : null}
      <ImageSlotsRow
        urls={urls}
        setUrls={setUrls}
        accept={VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT}
        constraints={constraints}
        t={t}
      />
    </div>
  );
}
